import { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ImageSearch as AnalyzeIcon,
  Upload as UploadIcon,
  Link as LinkIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Movie as MovieIcon,
  PhotoCamera as PhotoIcon,
} from '@mui/icons-material';
import { ImageAnalysisService } from '../services/imageAnalysisService';
import {
  ImageAnalysisSession,
  ImageAnalysisSessionStorage,
  AnalyzeImageRequest,
  ANALYSIS_PROMPTS,
  CINEMATIC_STYLES,
} from '../models/imageAnalysis';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`image-analysis-tabpanel-${index}`}
      aria-labelledby={`image-analysis-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: { xs: 2, sm: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ImageAnalysis: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [tabValue, setTabValue] = useState(0);
  const [imageSource, setImageSource] = useState<'upload' | 'url'>('upload');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [maxTokens, setMaxTokens] = useState(1024);
  const [temperature, setTemperature] = useState(0.7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSession, setCurrentSession] = useState<ImageAnalysisSession | null>(null);
  const [sessions, setSessions] = useState<ImageAnalysisSession[]>([]);
  const [cinematicDialog, setCinematicDialog] = useState(false);
  const [cinematicStyle, setCinematicStyle] = useState('');
  const [cinematicAudience, setCinematicAudience] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load sessions from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('imageAnalysisSessions');
    if (stored) {
      const data: ImageAnalysisSessionStorage = JSON.parse(stored);
      setSessions(data.sessions.map(s => ({
        ...s,
        createdAt: new Date(s.createdAt)
      })));
    }
  }, []);

  // Save sessions to localStorage
  const saveSessions = (newSessions: ImageAnalysisSession[]) => {
    const storage: ImageAnalysisSessionStorage = {
      sessions: newSessions,
      lastUpdated: new Date()
    };
    localStorage.setItem('imageAnalysisSessions', JSON.stringify(storage));
    setSessions(newSessions);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUrlChange = (url: string) => {
    setImageUrl(url);
    if (url) {
      setImagePreview(url);
    }
  };

  const handleAnalyze = async () => {
    if (!prompt.trim()) {
      setError('Please provide an analysis prompt');
      return;
    }

    if (imageSource === 'upload' && !imageFile) {
      setError('Please select an image file');
      return;
    }

    if (imageSource === 'url' && !imageUrl) {
      setError('Please provide an image URL');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: AnalyzeImageRequest = {
        userPrompt: prompt.trim(),
        maxResponseTokens: maxTokens,
        temperature
      };

      let response;
      if (imageSource === 'upload' && imageFile) {
        const base64 = await ImageAnalysisService.fileToBase64(imageFile);
        request.imageBytes = base64;
        response = await ImageAnalysisService.analyzeImageBytes(request);
      } else {
        request.imageUrl = imageUrl;
        response = await ImageAnalysisService.analyzeImageUrl(request);
      }

      const newSession: ImageAnalysisSession = {
        id: Date.now().toString(),
        imageUrl: imageSource === 'url' ? imageUrl : undefined,
        imageData: imagePreview || undefined,
        prompt: prompt.trim(),
        analysis: response,
        createdAt: new Date()
      };

      setCurrentSession(newSession);
      saveSessions([newSession, ...sessions]);
      setTabValue(1); // Switch to results tab
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze image');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCinematic = async () => {
    if (!currentSession?.analysis?.rawLlMOutput) return;

    setLoading(true);
    setError(null);

    try {
      const response = await ImageAnalysisService.generateCinematicSummary({
        imageAnalysis: currentSession.analysis.rawLlMOutput,
        style: cinematicStyle || undefined,
        targetAudience: cinematicAudience || undefined,
        maxTokens
      });

      if (response.cinematicSummary) {
        const updatedSession = {
          ...currentSession,
          cinematicSummary: response.cinematicSummary
        };

        setCurrentSession(updatedSession);
        
        const updatedSessions = sessions.map(s => 
          s.id === currentSession.id ? updatedSession : s
        );
        saveSessions(updatedSessions);
      }

      setCinematicDialog(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate cinematic summary');
    } finally {
      setLoading(false);
    }
  };

  const handleExportAnalysis = (session: ImageAnalysisSession) => {
    let content = `# Image Analysis Report\n\n`;
    content += `**Date:** ${session.createdAt.toLocaleDateString()}\n`;
    content += `**Prompt:** ${session.prompt}\n\n`;

    if (session.analysis) {
      if (session.analysis.title) {
        content += `## ${session.analysis.title}\n\n`;
      }

      if (session.analysis.description) {
        content += `### Description\n${session.analysis.description}\n\n`;
      }

      if (session.analysis.objects && session.analysis.objects.length > 0) {
        content += `### Objects Detected\n`;
        session.analysis.objects.forEach(obj => {
          content += `- ${obj}\n`;
        });
        content += '\n';
      }

      if (session.analysis.colors && session.analysis.colors.length > 0) {
        content += `### Color Palette\n`;
        session.analysis.colors.forEach(color => {
          content += `- ${color}\n`;
        });
        content += '\n';
      }

      if (session.analysis.composition) {
        content += `### Composition\n${session.analysis.composition}\n\n`;
      }

      if (session.analysis.mood) {
        content += `### Mood\n${session.analysis.mood}\n\n`;
      }

      if (session.analysis.style) {
        content += `### Style\n${session.analysis.style}\n\n`;
      }

      if (session.analysis.rawLlMOutput) {
        content += `### Detailed Analysis\n${session.analysis.rawLlMOutput}\n\n`;
      }
    }

    if (session.cinematicSummary) {
      content += `## Cinematic Summary\n${session.cinematicSummary}\n`;
    }

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `image-analysis-${session.id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyText = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleLoadSession = (session: ImageAnalysisSession) => {
    setCurrentSession(session);
    setImagePreview(session.imageData || session.imageUrl || null);
    setPrompt(session.prompt);
    setTabValue(1); // Switch to results tab
  };

  const handleDeleteSession = (sessionId: string) => {
    saveSessions(sessions.filter(s => s.id !== sessionId));
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
      setTabValue(0);
    }
  };

  const handleReset = () => {
    setImageFile(null);
    setImageUrl('');
    setImagePreview(null);
    setPrompt('');
    setCurrentSession(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
          }}
        >
          <AnalyzeIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Image Analysis' : 'Image Analysis AI'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile 
            ? 'AI-powered image analysis' 
            : 'Analyze images with AI-powered vision and generate detailed descriptions'}
        </Typography>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Tabs 
            value={tabValue} 
            onChange={(_, newValue) => setTabValue(newValue)} 
            variant={isMobile ? "fullWidth" : "standard"}
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                minHeight: 48,
              },
              ...(isTablet && {
                '& .MuiTab-iconWrapper': {
                  display: 'none',
                },
              }),
            }}
          >
            <Tab label={isMobile ? "Analyze" : "Analyze Image"} />
            <Tab label="Results" disabled={!currentSession} />
            <Tab label="History" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
                  >
                    Image Input
                  </Typography>
                  
                  <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <Button
                      variant={imageSource === 'upload' ? 'contained' : 'outlined'}
                      onClick={() => setImageSource('upload')}
                      sx={{ 
                        mr: 1, 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        minHeight: 36
                      }}
                    >
                      {isMobile ? 'Upload' : 'Upload File'}
                    </Button>
                    <Button
                      variant={imageSource === 'url' ? 'contained' : 'outlined'}
                      onClick={() => setImageSource('url')}
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        minHeight: 36
                      }}
                    >
                      {isMobile ? 'URL' : 'Image URL'}
                    </Button>
                  </Box>

                  {imageSource === 'upload' ? (
                    <Box>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        id="image-upload"
                      />
                      <label htmlFor="image-upload">
                        <Button
                          component="span"
                          variant="outlined"
                          startIcon={!isMobile && <UploadIcon />}
                          fullWidth
                          sx={{ 
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: 44
                          }}
                        >
                          {isMobile ? 'Select Image' : 'Select Image'}
                        </Button>
                      </label>
                      {imageFile && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mt: 1,
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }}
                        >
                          {isMobile && imageFile.name.length > 25 
                            ? `${imageFile.name.substring(0, 25)}...` 
                            : `Selected: ${imageFile.name}`}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      label="Image URL"
                      placeholder={isMobile ? "https://..." : "https://example.com/image.jpg"}
                      value={imageUrl}
                      onChange={(e) => handleUrlChange(e.target.value)}
                      InputProps={{
                        startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary', fontSize: { xs: 20, sm: 24 } }} />,
                        sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                      }}
                      InputLabelProps={{
                        sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                      }}
                    />
                  )}

                  {imagePreview && (
                    <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                      >
                        Preview
                      </Typography>
                      <Box 
                        component="img"
                        src={imagePreview}
                        alt="Preview"
                        sx={{ 
                          width: '100%', 
                          maxHeight: { xs: 200, sm: 300 },
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
                  >
                    Analysis Settings
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 2 : 3}
                    label="Analysis Prompt"
                    placeholder={isMobile ? "What to analyze?" : "What would you like to know about this image?"}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    sx={{ mb: { xs: 1.5, sm: 2 } }}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />

                  <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                    >
                      Quick Prompts
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {ANALYSIS_PROMPTS.slice(0, isMobile ? 3 : 4).map((p) => (
                        <Chip
                          key={p}
                          label={isMobile && p.length > 20 ? `${p.substring(0, 20)}...` : p}
                          size="small"
                          onClick={() => setPrompt(p)}
                          clickable
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <Typography 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {isMobile ? `Tokens: ${maxTokens}` : `Max Tokens: ${maxTokens}`}
                    </Typography>
                    <Slider
                      value={maxTokens}
                      onChange={(_, value) => setMaxTokens(value as number)}
                      min={256}
                      max={2048}
                      step={256}
                      marks={isMobile ? [] : [
                        { value: 256, label: '256' },
                        { value: 1024, label: '1024' },
                        { value: 2048, label: '2048' }
                      ]}
                      valueLabelDisplay="auto"
                    />
                  </Box>

                  <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <Typography 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Temperature: {temperature}
                    </Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, value) => setTemperature(value as number)}
                      min={0}
                      max={1}
                      step={0.1}
                      marks={isMobile ? [] : [
                        { value: 0, label: 'Factual' },
                        { value: 0.5, label: 'Balanced' },
                        { value: 1, label: 'Creative' }
                      ]}
                      valueLabelDisplay="auto"
                    />
                  </Box>

                  {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: { xs: 1.5, sm: 2 } }}>
                      <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                      </Typography>
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 } }}>
                    <Button
                      variant="contained"
                      onClick={handleAnalyze}
                      disabled={loading || !prompt.trim() || (!imageFile && !imageUrl)}
                      startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <AnalyzeIcon />)}
                      fullWidth
                      sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        minHeight: 44
                      }}
                    >
                      {loading ? 'Analyzing...' : (isMobile ? 'Analyze' : 'Analyze Image')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleReset}
                      disabled={loading}
                      sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        minHeight: 36
                      }}
                    >
                      Reset
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {currentSession && (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={4}>
                {currentSession.imageData || currentSession.imageUrl ? (
                  <Card>
                    <CardMedia
                      component="img"
                      image={currentSession.imageData || currentSession.imageUrl}
                      alt="Analyzed image"
                      sx={{ maxHeight: { xs: 250, sm: 400 }, objectFit: 'contain' }}
                    />
                  </Card>
                ) : (
                  <Card sx={{ height: { xs: 200, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PhotoIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary' }} />
                  </Card>
                )}
              </Grid>

              <Grid item xs={12} md={8}>
                {currentSession.analysis && (
                  <Box>
                    {currentSession.analysis.title && (
                      <Typography 
                        variant="h5" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
                      >
                        {currentSession.analysis.title}
                      </Typography>
                    )}

                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography 
                          variant="h6"
                          sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
                        >
                          {isMobile ? 'Results' : 'Analysis Results'}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {currentSession.analysis.description && (
                          <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                            <Typography 
                              variant="subtitle2" 
                              color="primary" 
                              gutterBottom
                              sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                            >
                              Description
                            </Typography>
                            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {currentSession.analysis.description}
                            </Typography>
                          </Box>
                        )}

                        {currentSession.analysis.objects && currentSession.analysis.objects.length > 0 && (
                          <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                            <Typography 
                              variant="subtitle2" 
                              color="primary" 
                              gutterBottom
                              sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                            >
                              {isMobile ? 'Objects' : 'Objects Detected'}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {currentSession.analysis.objects.map((obj, i) => (
                                <Chip 
                                  key={i} 
                                  label={obj} 
                                  size="small" 
                                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {currentSession.analysis.rawLlMOutput && (
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography 
                                variant="subtitle2" 
                                color="primary" 
                                gutterBottom
                                sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                              >
                                {isMobile ? 'Analysis' : 'Detailed Analysis'}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => handleCopyText(currentSession.analysis!.rawLlMOutput!)}
                                title={isMobile ? "" : "Copy to clipboard"}
                                sx={{ minHeight: 36, minWidth: 36 }}
                              >
                                <CopyIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                              </IconButton>
                            </Box>
                            <Typography 
                              sx={{ 
                                whiteSpace: 'pre-wrap', 
                                fontSize: { xs: '0.875rem', sm: '0.95rem' }
                              }}
                            >
                              {currentSession.analysis.rawLlMOutput}
                            </Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>

                    {currentSession.cinematicSummary && (
                      <Accordion sx={{ mt: { xs: 1.5, sm: 2 } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography 
                            variant="h6"
                            sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
                          >
                            {isMobile ? 'Cinematic' : 'Cinematic Summary'}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography 
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                          >
                            {currentSession.cinematicSummary}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    <Box sx={{ mt: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 } }}>
                      <Button
                        variant="contained"
                        startIcon={!isMobile && <MovieIcon />}
                        onClick={() => setCinematicDialog(true)}
                        disabled={!currentSession.analysis?.rawLlMOutput}
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          minHeight: 44
                        }}
                      >
                        {isMobile ? 'Generate Cinematic' : 'Generate Cinematic'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={!isMobile && <DownloadIcon />}
                        onClick={() => handleExportAnalysis(currentSession)}
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          minHeight: 44
                        }}
                      >
                        {isMobile ? 'Export' : 'Export Analysis'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {sessions.length > 0 ? (
            <List>
              {sessions.map((session) => (
                <ListItem 
                  key={session.id} 
                  divider
                  sx={{ cursor: 'pointer', p: { xs: 1.5, sm: 2 } }}
                  onClick={() => handleLoadSession(session)}
                >
                  <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, width: '100%', alignItems: 'center' }}>
                    {session.imageData || session.imageUrl ? (
                      <Box
                        component="img"
                        src={session.imageData || session.imageUrl}
                        sx={{ 
                          width: { xs: 50, sm: 60 }, 
                          height: { xs: 50, sm: 60 }, 
                          objectFit: 'cover', 
                          borderRadius: 1 
                        }}
                      />
                    ) : (
                      <Box sx={{ 
                        width: { xs: 50, sm: 60 }, 
                        height: { xs: 50, sm: 60 }, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        backgroundColor: 'grey.200', 
                        borderRadius: 1 
                      }}>
                        <PhotoIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                      </Box>
                    )}
                    <ListItemText
                      primary={
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
                          {isMobile && session.prompt.length > 40 
                            ? `${session.prompt.substring(0, 40)}...` 
                            : session.prompt}
                        </Typography>
                      }
                      secondary={
                        <Typography 
                          variant="caption"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          {session.analysis?.title && !isMobile && `${session.analysis.title} • `}
                          {isMobile 
                            ? session.createdAt.toLocaleDateString()
                            : session.createdAt.toLocaleDateString()}
                        </Typography>
                      }
                    />
                  </Box>
                  <ListItemSecondaryAction>
                    <Tooltip title={isMobile ? "" : "Export"}>
                      <IconButton 
                        edge="end" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportAnalysis(session);
                        }}
                        sx={{ mr: { xs: 0, sm: 1 }, minHeight: 44, minWidth: 44 }}
                      >
                        <DownloadIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isMobile ? "" : "Delete"}>
                      <IconButton 
                        edge="end" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(session.id);
                        }}
                        sx={{ minHeight: 44, minWidth: 44 }}
                      >
                        <DeleteIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
              <AnalyzeIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: { xs: 1.5, sm: 2 } }} />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                No analysis history yet
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                Your image analyses will appear here
              </Typography>
            </Box>
          )}
        </TabPanel>
        </Box>
      </Paper>

      {/* Cinematic Dialog */}
      <Dialog 
        open={cinematicDialog} 
        onClose={() => setCinematicDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          {isMobile ? 'Cinematic Summary' : 'Generate Cinematic Summary'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Cinematic Style
              </InputLabel>
              <Select
                value={cinematicStyle}
                onChange={(e) => setCinematicStyle(e.target.value)}
                label="Cinematic Style"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Auto-detect
                </MenuItem>
                {CINEMATIC_STYLES.map((style) => (
                  <MenuItem key={style} value={style} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {style}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Target Audience"
              placeholder={isMobile ? "e.g., General audience" : "e.g., General audience, Film enthusiasts, Art critics"}
              value={cinematicAudience}
              onChange={(e) => setCinematicAudience(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setCinematicDialog(false)}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 36
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleGenerateCinematic} 
            variant="contained" 
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44
            }}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ImageAnalysis;